import React, { SVGProps, MouseEventHandler } from 'react';
import { Skeleton } from '@luminalearning/components';

type LargeCardProps = {
    /** An image to display on the card. This is optional. */
    image?: string | ((value: SVGProps<SVGSVGElement>) => React.ReactElement);
    /** Alt text to apply to the card image.
    This is optional and should be ommited if the image is purely decorative/redundant. */
    imageAltText?: string;
    /** The heading to be displayed in bold on the card. */
    heading: string;
    /** The body text to be displayed below the heading. */
    body: string;
    /** The MouseEventHandler to handle any onClick events. */
    onClick: MouseEventHandler<HTMLButtonElement>;
    /** If true, grey out the card and make it not clickable. */
    isDisabled?: boolean;
    /** Class name to pass to the base element in the component. */
    className?: string;
    /** Whether the card content is loading. */
    isLoading?: boolean;
};

/**
    Cards are content containers which are clickable.
    It's crucial that the entire area of the card is clickable,
    so for this reason Cards cannot contain other clickable UI
    elements, they can only contain text and image content.
    Clickability is signified by drop shadow effects, giving
    Cards a sense of pressability.
* */
const LargeCard = (props: LargeCardProps) => {
    const {
        image: CardImage,
        imageAltText = '',
        heading,
        body,
        onClick,
        isDisabled = false,
        className = '',
        isLoading,
    } = props;

    return (
        <button
            className={([
                'flex flex-col p-6 gap-y-6',
                'bg-white border-1.5 shadow-card border-card-normal rounded-md',
                isDisabled ? 'opacity-50' : '',
                (isDisabled || isLoading
                    ? 'cursor-not-allowed'
                    : 'hover:border-card-hover outline-0 focus-visible:border-card-hover active:shadow-none'
                ),
                className,
            ].join(' '))}
            style={{
                height: '19rem',
                width: '22rem',
            }}
            onClick={onClick}
            disabled={ isDisabled || isLoading }
            type='button'
        >
            { !isLoading && CardImage && typeof CardImage === 'string'
                    && <img src={ CardImage } alt={ imageAltText } className='place-self-center h-16'></img>
            }
            { !isLoading && CardImage && typeof CardImage !== 'string'
                    && <div className='place-self-center'>
                        <CardImage height='4rem' />
                    </div>
            }
            { isLoading
                    && <div className='place-self-center'>
                        <Skeleton circle={true} style={{ width: '4rem', height: '4rem' }} />
                    </div>
            }
            <div className='text-start grow w-full'>
                <h2 className='text-headline-reg font-bold break-normal' style={{ lineHeight: '2.813rem' }}>
                    { isLoading ? <Skeleton /> : heading }
                </h2>
                <p className='overflow-hidden text-ellipsis whitespace-normal mt-1'>
                    { isLoading ? <Skeleton count={3} /> : body }
                </p>
            </div>
        </button>
    );
};

LargeCard.defaultProps = {
    imageAltText: '',
    isDisabled: false,
    isLoading: false,
};

export default LargeCard;